/* Reuses Hyper's product ID history and native drawer lifecycle. */
(() => {
  'use strict';

  const HISTORY_KEY = 'hypertheme:recently-viewed';
  const HISTORY_LIMIT = 30;
  const ELEMENT_NAME = 'dz-recently-viewed-drawer';

  function productId(value) {
    if (typeof value !== 'number' && (typeof value !== 'string' || !/^\d+$/.test(value))) return null;
    const id = Number(value);
    return Number.isSafeInteger(id) && id > 0 ? id : null;
  }

  function readHistory() {
    try {
      const value = JSON.parse(window.localStorage.getItem(HISTORY_KEY) || '[]');
      if (!Array.isArray(value)) throw new Error('Invalid recently viewed history.');
      const ids = [];
      const seen = new Set();
      for (const valueId of value) {
        const id = productId(valueId);
        if (id === null || seen.has(id)) continue;
        seen.add(id);
        ids.push(id);
        if (ids.length === HISTORY_LIMIT) break;
      }
      return { ids, error: false };
    } catch (_) {
      return { ids: [], error: true };
    }
  }

  function ensureProductTracking() {
    const metadata = document.getElementById('DzRecentlyViewedProduct');
    if (!metadata || document.querySelector('product-recently-viewed')) return;
    try {
      const id = productId(JSON.parse(metadata.textContent).id);
      if (id === null) return;
      const tracker = document.createElement('product-recently-viewed');
      tracker.dataset.productId = String(id);
      tracker.hidden = true;
      document.body.appendChild(tracker);
    } catch (_) {
      // Missing product metadata must not interrupt the storefront.
    }
  }

  const nextTask = () => new Promise((resolve) => window.setTimeout(resolve, 0));

  function transitionTime(element) {
    if (!element) return 0;
    const style = window.getComputedStyle(element);
    const milliseconds = (value) => value.trim().endsWith('ms') ? parseFloat(value) : parseFloat(value) * 1000;
    const durations = style.transitionDuration.split(',').map(milliseconds);
    const delays = style.transitionDelay.split(',').map(milliseconds);
    return Math.max(0, ...durations.map((duration, index) => duration + (delays[index % delays.length] || 0)));
  }

  customElements.whenDefined('drawer-component').then(() => {
    if (customElements.get(ELEMENT_NAME)) return;
    const NativeDrawer = customElements.get('drawer-component');
    const NativeModal = Object.getPrototypeOf(NativeDrawer.prototype).constructor;

    class DzRecentlyViewedDrawer extends NativeDrawer {
      constructor() {
        super();
        this.intent = 0;
        this.requestNumber = 0;
        this.pendingOpen = false;
        this.lockHeld = false;
      }

      get requiresBodyAppended() {
        return false;
      }

      get controls() {
        // Delegate clicks so controls added by the theme editor work immediately.
        return this.skipNativeControlBinding ? [] : super.controls;
      }

      connectedCallback() {
        // Render with the header, then mount at body level to escape sticky-header stacking contexts.
        if (this.parentElement !== document.body) {
          this.ownerSection = this.closest('.shopify-section');
          this.portaling = true;
          try {
            document.body.appendChild(this);
          } finally {
            this.portaling = false;
          }
          return;
        }
        // Theme-editor replacement can create the next instance before removing the old header.
        document.querySelectorAll(ELEMENT_NAME).forEach((drawer) => {
          if (drawer !== this && drawer.id === this.id) drawer.remove();
        });
        this.skipNativeControlBinding = true;
        try {
          super.connectedCallback();
        } finally {
          this.skipNativeControlBinding = false;
        }
        const options = { signal: this.abortController.signal };
        this.content = this.querySelector('[data-dz-rv-content]');
        this.status = this.querySelector('[data-dz-rv-status]');
        this.retry = this.querySelector('[data-dz-rv-retry]');
        this.count = this.querySelector('[data-dz-rv-count]');

        document.addEventListener('click', (event) => {
          const control = event.target instanceof Element ? event.target.closest('[aria-controls]') : null;
          if (!control || control.getAttribute('aria-controls') !== this.id) return;
          this.onButtonClick({
            currentTarget: control,
            target: event.target,
            preventDefault: () => event.preventDefault(),
          });
        }, options);
        document.addEventListener('shopify:section:load', () => this.syncControls(), options);
        document.addEventListener('shopify:section:unload', (event) => {
          if (event.target === this.ownerSection) this.remove();
        }, options);
        window.addEventListener('storage', (event) => {
          if (event.key === HISTORY_KEY || event.key === null) this.refreshIfOpen();
        }, options);
        window.addEventListener('dz:recently-viewed:updated', () => this.refreshIfOpen(), options);
        if (this.retry) this.retry.addEventListener('click', () => this.refresh(), options);
        this.syncControls();
      }

      disconnectedCallback() {
        if (this.portaling) return;
        this.hide();
        this.cancelTransition();
        if (this.abortController) super.disconnectedCallback();
      }

      syncControls() {
        this.controls.forEach((control) => control.setAttribute('aria-expanded', String(this.open)));
      }

      onButtonClick(event) {
        if (this.pendingOpen && !this.open) {
          event.preventDefault();
          if (!event.currentTarget.disabled) this.hide();
          return;
        }
        super.onButtonClick(event);
      }

      show(activeElement = null, animate = true) {
        if (this.open) return super.show(activeElement, animate);
        const intent = ++this.intent;
        this.pendingOpen = true;
        return (async () => {
          if (this.closingPromise) await this.closingPromise;
          if (intent !== this.intent || !this.isConnected) return;
          await this.closeOtherPanels();
          if (intent !== this.intent || !this.isConnected) return;
          this.pendingOpen = false;
          const showing = super.show(activeElement, animate);
          this.refresh();
          return showing;
        })().finally(() => {
          if (intent === this.intent) this.pendingOpen = false;
        });
      }

      hide() {
        ++this.intent;
        this.pendingOpen = false;
        this.abortRequest();
        const hiding = super.hide();
        if (!hiding) return this.closingPromise;
        // Native focus restoration and lock decrement run in a queued task.
        const closing = Promise.resolve(hiding).then(nextTask);
        this.closingPromise = closing;
        closing.finally(() => {
          if (this.closingPromise === closing) this.closingPromise = null;
        });
        return closing;
      }

      async closeOtherPanels() {
        if (document.body.classList.contains('search-open')) {
          const closeButtons = Array.from(document.querySelectorAll('.header__search-close'));
          const visibleClose = closeButtons.find((button) => button.getClientRects().length > 0);
          // The permanent desktop search hides its close control, but its handler still closes it.
          const closeSearch = visibleClose || closeButtons[0];
          if (closeSearch) closeSearch.click();
        }
        const panels = Array.from(document.querySelectorAll('[open]')).filter((panel) => (
          panel !== this && panel instanceof NativeModal && panel.open
        ));
        await Promise.all(panels.map(async (panel) => {
          const hidden = panel.hide();
          if (!hidden || typeof hidden.then !== 'function') return;
          // Older native drawers wait only for transitionend, including at zero duration.
          const timeout = window.setTimeout(() => {
            if (!panel.open && panel.overlay) panel.overlay.dispatchEvent(new Event('transitionend'));
          }, Math.min(2500, transitionTime(panel.overlay) + 160));
          try {
            await hidden;
          } finally {
            window.clearTimeout(timeout);
          }
        }));
        await nextTask();
      }

      handleAfterShow() {
        if (!this.open || !this.isConnected || this.lockHeld) return;
        this.lockHeld = true;
        super.handleAfterShow();
      }

      handleAfterHide() {
        if (this.open) return;
        if (this.lockHeld) {
          this.lockHeld = false;
          super.handleAfterHide();
        }
        const anotherOpenModal = Array.from(document.querySelectorAll('[open]')).some((panel) => (
          panel !== this && panel instanceof NativeModal && panel.isLockingNeeded
        ));
        if (!anotherOpenModal) document.body.classList.remove(this.classes.showing);
      }

      cancelTransition() {
        if (this.finishTransition) this.finishTransition();
      }

      handleShowTransition(animate = true) {
        return this.runTransition(true, animate);
      }

      handleHideTransition() {
        return this.runTransition(false, true);
      }

      runTransition(show, animate) {
        this.cancelTransition();
        const overlay = this.overlay;
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!overlay || !this.isConnected || !animate || reduced) {
          this.toggleAttribute('active', show);
          return Promise.resolve();
        }
        return new Promise((resolve) => {
          let timer;
          let frame;
          let finished = false;
          const finish = () => {
            if (finished) return;
            finished = true;
            window.clearTimeout(timer);
            window.cancelAnimationFrame(frame);
            overlay.removeEventListener('transitionend', onEnd);
            if (this.finishTransition === finish) this.finishTransition = null;
            resolve();
          };
          const onEnd = (event) => {
            if (event.target === overlay && (!event.propertyName || event.propertyName === 'opacity')) finish();
          };
          this.finishTransition = finish;
          overlay.addEventListener('transitionend', onEnd);
          const change = () => {
            this.toggleAttribute('active', show);
            const duration = transitionTime(overlay);
            timer = window.setTimeout(finish, Math.min(2500, duration + 120));
          };
          if (show) {
            // Flush the initial closed position before starting the slide.
            void overlay.offsetWidth;
            frame = window.requestAnimationFrame(change);
          } else {
            change();
          }
        });
      }

      abortRequest() {
        ++this.requestNumber;
        if (this.requestController) this.requestController.abort();
        this.requestController = null;
      }

      refreshIfOpen() {
        if (this.open) this.refresh();
      }

      setState(state, count = 0) {
        const fallback = {
          loading: 'Loading your recently viewed products…',
          empty: 'Your recently viewed products will appear here.',
          error: 'We could not load your recently viewed products. Please try again.',
          unavailable: 'These recently viewed products are currently unavailable.',
        };
        if (this.status) {
          this.status.textContent = state === 'ready' ? '' : (this.dataset[`${state}Text`] || fallback[state] || '');
          this.status.hidden = state === 'ready';
        }
        if (this.count) this.count.textContent = (this.dataset.countText || '{count} products').replace(/\{count\}/g, String(count));
        if (this.retry) {
          this.retry.hidden = state !== 'error';
          this.retry.disabled = state !== 'error';
        }
        if (this.content) this.content.setAttribute('aria-busy', String(state === 'loading'));
        if (this.lockHeld && this.open && !Shopify.designMode) {
          // Native focus endpoints are a snapshot; refresh them after asynchronous cards change.
          const focused = document.activeElement;
          const keepFocus = focused && this.contains(focused) && !focused.closest('[hidden], [inert]') && focused.getClientRects().length > 0;
          FoxTheme.a11y.trapFocus(this, keepFocus ? focused : this.focusElement);
        }
      }

      async refresh() {
        this.abortRequest();
        if (!this.content || !this.open) return;
        const requestNumber = this.requestNumber;
        const history = readHistory();
        this.content.replaceChildren();
        if (history.error) {
          this.setState('error');
          return;
        }
        if (!history.ids.length) {
          this.setState('empty');
          return;
        }
        this.setState('loading', history.ids.length);
        const controller = new AbortController();
        this.requestController = controller;
        let timedOut = false;
        let requestTimeout;
        try {
          if (!this.dataset.searchUrl || !this.dataset.sectionId) throw new Error('Missing recently viewed results section.');
          const url = new URL(this.dataset.searchUrl, window.location.origin);
          url.searchParams.set('section_id', this.dataset.sectionId);
          url.searchParams.set('type', 'product');
          url.searchParams.set('q', history.ids.map((id) => `id:${id}`).join(' OR '));
          requestTimeout = window.setTimeout(() => {
            timedOut = true;
            controller.abort();
          }, 10000);
          const response = await fetch(url.toString(), { signal: controller.signal, credentials: 'same-origin' });
          if (!response.ok) throw new Error('Recently viewed request failed.');
          const html = await response.text();
          if (requestNumber !== this.requestNumber || !this.open) return;
          if (controller.signal.aborted) {
            if (timedOut) throw new Error('Recently viewed request timed out.');
            return;
          }
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const results = parsed.querySelector('[data-dz-rv-results]');
          if (!results) throw new Error('Recently viewed results section is unavailable.');
          const cards = new Map();
          results.querySelectorAll('[data-dz-rv-product-id]').forEach((card) => {
            const id = productId(card.dataset.dzRvProductId);
            if (id !== null && !cards.has(id)) cards.set(id, card);
          });
          const fragment = document.createDocumentFragment();
          let displayed = 0;
          history.ids.forEach((id) => {
            if (!cards.has(id)) return;
            fragment.appendChild(document.importNode(cards.get(id), true));
            ++displayed;
          });
          this.content.replaceChildren(fragment);
          this.setState(displayed ? 'ready' : 'unavailable', displayed);
        } catch (error) {
          if (requestNumber !== this.requestNumber || !this.open || (controller.signal.aborted && !timedOut)) return;
          this.setState('error');
        } finally {
          window.clearTimeout(requestTimeout);
          if (this.requestController === controller) this.requestController = null;
        }
      }
    }

    customElements.define(ELEMENT_NAME, DzRecentlyViewedDrawer);
    ensureProductTracking();
  });
})();
